// GLBA / Regulation P financial privacy notice for JC3 Wealth Legacy Group LLC.
// Structure reproduces the CFPB Model Privacy Form (12 CFR Part 1016, Appendix A).
// Standard boilerplate is verbatim; only company name, contact, and the YES/NO
// answers are substituted. Attorney should confirm against the official CFPB form.
import { useEffect } from "react";
import MarketingShell from "../components/MarketingShell";

const COMPANY = "JC3 Wealth Legacy Group LLC";
const PHONE = "470-888-7331";
const EMAIL = "jace@jc3wealthlegacy.com";

const SHARE_ROWS = [
  {
    reason:
      "For our everyday business purposes — such as to process your transactions, maintain your account(s), respond to court orders and legal investigations, or report to consumer reporting agencies",
    shares: "Yes",
    limit: "No",
  },
  {
    reason: "For our marketing purposes — to offer our products and services to you",
    shares: "No",
    limit: "We don't share",
  },
  {
    reason: "For joint marketing with other financial companies",
    shares: "No",
    limit: "We don't share",
  },
  {
    reason:
      "For our affiliates' everyday business purposes — information about your transactions and experiences",
    shares: "No",
    limit: "We don't share",
  },
  {
    reason:
      "For our affiliates' everyday business purposes — information about your financial standing",
    shares: "No",
    limit: "We don't share",
  },
  {
    reason: "For our affiliates to market to you",
    shares: "No",
    limit: "We don't share",
  },
  {
    reason: "For nonaffiliates to market to you",
    shares: "No",
    limit: "We don't share",
  },
];

const FactRow = ({ label, children }) => (
  <div className="grid grid-cols-1 md:grid-cols-12 gap-2 md:gap-6 py-4 border-t border-jc-gold-soft">
    <div className="md:col-span-2 font-eyebrow text-jc-burgundy">{label}</div>
    <div className="md:col-span-10 font-body text-[15px] text-jc-ink leading-[1.7]">{children}</div>
  </div>
);

const QA = ({ q, children }) => (
  <div className="py-4 border-t border-jc-gold-soft">
    <p className="font-display text-jc-forest text-lg leading-snug">{q}</p>
    <p className="mt-2 font-body text-[15px] text-jc-ink leading-[1.7]">{children}</p>
  </div>
);

export default function GlbaPrivacyNotice() {
  useEffect(() => { document.title = "Financial Privacy Notice | JC3 Wealth Legacy Group"; }, []);
  return (
    <MarketingShell>
      <section data-testid="glba-privacy-page" className="on-stone">
        {/* Hero */}
        <div className="page-head">
          <div className="wrap" style={{ display: "grid", gap: "var(--s4)" }}>
            <p className="small dim-dark">{COMPANY}</p>
            <h1 className="display-xl">Financial Privacy Notice</h1>
            <p className="small dim-dark">
              GLBA / Regulation P Financial Privacy Notice · Rev. January 25, 2026
            </p>
          </div>
        </div>

        <div className="mx-auto max-w-[1100px] px-6 md:px-10 py-16 md:py-20 space-y-6">
          {/* FACTS box */}
          <div className="jc-card p-8 md:p-10">
            <div className="flex flex-col md:flex-row md:items-baseline gap-2 md:gap-6">
              <div className="font-display text-jc-gold text-3xl">FACTS</div>
              <h2
                className="font-display text-jc-forest leading-[1.15]"
                style={{ fontSize: "clamp(1.3rem, 2.4vw, 1.9rem)" }}
              >
                WHAT DOES {COMPANY.toUpperCase()} DO WITH YOUR PERSONAL INFORMATION?
              </h2>
            </div>

            <FactRow label="Why?">
              Financial companies choose how they share your personal information. Federal law gives
              consumers the right to limit some but not all sharing. Federal law also requires us to
              tell you how we collect, share, and protect your personal information. Please read this
              notice carefully to understand what we do.
            </FactRow>
            <FactRow label="What?">
              The types of personal information we collect and share depend on the product or service
              you have with us. This information can include Social Security number and date of birth,
              income and account balances, and financial history and transaction history. When you are no
              longer our customer, we continue to share your information as described in this notice.
            </FactRow>
            <FactRow label="How?">
              All financial companies need to share customers' personal information to run their
              everyday business. In the section below, we list the reasons financial companies can
              share their customers' personal information; the reasons {COMPANY} chooses to share; and
              whether you can limit this sharing.
            </FactRow>
          </div>

          {/* Sharing table */}
          <div className="jc-card p-2 md:p-4 overflow-x-auto">
            <table className="w-full border-collapse min-w-[640px]">
              <thead>
                <tr className="text-left align-bottom">
                  <th className="p-4 font-eyebrow text-jc-burgundy w-[55%]">
                    Reasons we can share your personal information
                  </th>
                  <th className="p-4 font-eyebrow text-jc-burgundy">Does {COMPANY} share?</th>
                  <th className="p-4 font-eyebrow text-jc-burgundy">Can you limit this sharing?</th>
                </tr>
              </thead>
              <tbody>
                {SHARE_ROWS.map((r) => (
                  <tr key={r.reason} className="border-t border-jc-gold-soft align-top">
                    <td className="p-4 font-body text-[14.5px] text-jc-ink leading-[1.6]">
                      {r.reason}
                    </td>
                    <td className="p-4 font-display text-jc-forest">{r.shares}</td>
                    <td className="p-4 font-body text-[14.5px] text-jc-ink">{r.limit}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* To limit / Questions */}
          <div className="jc-card p-8 md:p-10">
            <h2 className="font-display text-jc-forest text-2xl">To limit our sharing</h2>
            <p className="mt-3 font-body text-[15px] text-jc-ink leading-[1.7]">
              {COMPANY} does not share your personal information in any way that you would need to
              limit. If our practices change, we will notify you and provide any choices required by
              law before we share.
            </p>
          </div>

          <div className="jc-card p-8 md:p-10">
            <p className="font-eyebrow text-jc-burgundy">Questions?</p>
            <p className="mt-3 font-body text-[15.5px] text-jc-ink">
              Call{" "}
              <a
                className="text-jc-gold font-semibold whitespace-nowrap underline decoration-jc-gold/40 hover:decoration-jc-gold"
                href={`tel:+1${PHONE.replace(/\D/g, "").replace(/^1/, "")}`}
                data-testid="glba-phone-link"
              >
                {PHONE}
              </a>{" "}
              or email{" "}
              <a
                className="text-jc-gold underline decoration-jc-gold/40 hover:decoration-jc-gold"
                href={`mailto:${EMAIL}`}
              >
                {EMAIL}
              </a>
              .
            </p>
          </div>

          {/* Page 2 — Who we are / What we do / Definitions */}
          <div className="jc-card p-8 md:p-10">
            <h2 className="font-display text-jc-forest text-2xl">Who we are</h2>
            <QA q="Who is providing this notice?">{COMPANY}.</QA>
          </div>

          <div className="jc-card p-8 md:p-10">
            <h2 className="font-display text-jc-forest text-2xl">What we do</h2>
            <QA q={`How does ${COMPANY} protect my personal information?`}>
              To protect your personal information from unauthorized access and use, we use security
              measures that comply with federal law. These measures include encryption of data at rest
              and in transit, access controls, and limiting access to staff on a need-to-know basis.
            </QA>
            <QA q={`How does ${COMPANY} collect my personal information?`}>
              We collect your personal information, for example, when you open an account or apply for
              our services, give us your income or account information, or provide your contact
              information. We also collect your personal information from others, such as consumer
              reporting agencies.
            </QA>
            <QA q="Why can't I limit all sharing?">
              Federal law gives you the right to limit only sharing for affiliates' everyday business
              purposes — information about your financial standing; affiliates from using your
              information to market to you; and sharing for nonaffiliates to market to you. State laws
              and individual companies may give you additional rights to limit sharing.
            </QA>
          </div>

          <div className="jc-card p-8 md:p-10">
            <h2 className="font-display text-jc-forest text-2xl">Definitions</h2>
            <QA q="Affiliates">
              Companies related by common ownership or control. They can be financial and nonfinancial
              companies. {COMPANY} has no affiliates.
            </QA>
            <QA q="Nonaffiliates">
              Companies not related by common ownership or control. They can be financial and
              nonfinancial companies. {COMPANY} does not share with nonaffiliates so they can market to
              you.
            </QA>
            <QA q="Joint marketing">
              A formal agreement between nonaffiliated financial companies that together market
              financial products or services to you. {COMPANY} does not engage in joint marketing.
            </QA>
          </div>
        </div>
      </section>
    </MarketingShell>
  );
}
